import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { zoneLabel } from "@/lib/zones";
import { useLang } from "@/contexts/LangContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Lock, ArrowLeftRight } from "lucide-react";
import { toast } from "sonner";

const L = {
  en: { kicker: "CLUB · HEAD TO HEAD", title: "Compare players", subtitle: "Pick two players from your teams to see them side by side.", playerA: "Player A", playerB: "Player B", pick: "Select a player",
    scores: "Weekly AI scores", week: "Last 7 days", stats: "Physical stats", medical: "Injury history", video: "Video AI (avg)", private: "Private", none: "—", noData: "No data yet", same: "Pick two different players",
    readiness: "Readiness", mental: "Mental", physical: "Physical", injury_risk: "Injury risk", mood: "Mood", energy: "Energy", soreness: "Soreness", stress: "Stress", sleep_hours: "Sleep (h)", entries: "Logs", pain_days_30: "Pain days (30d)",
    weight_kg: "Weight", height_cm: "Height", top_speed_kmh: "Top speed", endurance_vo2: "VO2", muscle_index: "Muscle", explosive_power: "Power",
    total_injuries: "Injuries total", active_injuries: "Active", weaknesses: "Weak spots", technique: "Technique", decision_making: "Decision", count: "Analyses", alerts: "Pain alerts", history: "Recent" },
  fr: { kicker: "CLUB · FACE À FACE", title: "Comparer des joueurs", subtitle: "Choisis deux joueurs de tes équipes pour les voir côte à côte.", playerA: "Joueur A", playerB: "Joueur B", pick: "Choisir un joueur",
    scores: "Scores IA hebdo", week: "7 derniers jours", stats: "Stats physiques", medical: "Historique blessures", video: "Vidéo IA (moy.)", private: "Privé", none: "—", noData: "Pas encore de données", same: "Choisis deux joueurs différents",
    readiness: "Fraîcheur", mental: "Mental", physical: "Physique", injury_risk: "Risque blessure", mood: "Humeur", energy: "Énergie", soreness: "Courbatures", stress: "Stress", sleep_hours: "Sommeil (h)", entries: "Entrées", pain_days_30: "Jours douleur (30j)",
    weight_kg: "Poids", height_cm: "Taille", top_speed_kmh: "Vitesse max", endurance_vo2: "VO2", muscle_index: "Muscle", explosive_power: "Puissance",
    total_injuries: "Blessures total", active_injuries: "Actives", weaknesses: "Points faibles", technique: "Technique", decision_making: "Décision", count: "Analyses", alerts: "Alertes douleur", history: "Récent" },
};

// lower is better for these keys
const LOWER_BETTER = new Set(["injury_risk", "soreness", "stress", "pain_days_30", "total_injuries", "active_injuries", "weaknesses"]);
const NEUTRAL = new Set(["weight_kg", "height_cm", "entries", "count"]);

const Row = ({ label, a, b, k, t }) => {
  const num = (v) => (typeof v === "number" ? v : null);
  const na = num(a), nb = num(b);
  let winA = false, winB = false;
  if (!NEUTRAL.has(k) && na != null && nb != null && na !== nb) {
    const aBetter = LOWER_BETTER.has(k) ? na < nb : na > nb;
    winA = aBetter; winB = !aBetter;
  }
  const cell = (v, win) => <div className={`text-center font-display font-black text-xl ${win ? "text-[#00F076]" : v == null ? "text-slate-600" : "text-white"}`}>{v ?? t.none}</div>;
  return (
    <div className="grid grid-cols-3 items-center py-2 border-t border-[#1E2638]" data-testid={`compare-row-${k}`}>
      {cell(a, winA)}
      <div className="text-center text-[10px] font-mono-tech uppercase tracking-wider text-slate-400">{label}</div>
      {cell(b, winB)}
    </div>
  );
};

const Block = ({ title, keys, a, b, privA, privB, t, testid }) => {
  const cell = (data, priv, k, side) => {
    if (priv) return <span title={t.private} data-testid={`compare-private-${side}`}><Lock className="w-4 h-4 inline text-slate-600" /></span>;
    if (data === null) return <span className="text-[10px] font-mono-tech text-slate-600" data-testid={`compare-nodata-${side}`}>{t.noData}</span>;
    return data?.[k];
  };
  return (
    <div className="rounded-xl border border-[#1E2638] bg-[#121721] p-5" data-testid={testid}>
      <div className="font-display font-bold uppercase text-base mb-2 text-center">{title}</div>
      {privA && privB ? <div className="text-center text-slate-500 text-xs flex items-center justify-center gap-1"><Lock className="w-3 h-3" />{t.private}</div>
        : keys.map((k) => <Row key={k} k={k} label={t[k] || k} t={t} a={cell(a, privA, k, "a")} b={cell(b, privB, k, "b")} />)}
    </div>
  );
};

export default function ClubCompare() {
  const { lang } = useLang();
  const t = L[lang] || L.en;
  const [sp, setSp] = useSearchParams();
  const [players, setPlayers] = useState([]);
  const [a, setA] = useState(sp.get("a") || "");
  const [b, setB] = useState(sp.get("b") || "");
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/club/players").then((r) => setPlayers(r.data)); }, []);
  useEffect(() => {
    if (!a || !b) { setData(null); return; }
    if (a === b) { setData(null); toast.error(t.same); return; }
    setSp({ a, b });
    api.get(`/club/compare?a=${a}&b=${b}`).then((r) => setData(r.data)).catch((e) => { setData(null); toast.error(e?.response?.data?.detail || "Failed"); });
  }, [a, b, setSp]); // eslint-disable-line react-hooks/exhaustive-deps

  const Picker = ({ value, onChange, testid, label }) => (
    <div className="flex-1 min-w-[220px]">
      <div className="text-[10px] font-mono-tech uppercase tracking-wider text-slate-400 mb-1">{label}</div>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={testid} className="bg-[#121721] border-[#1E2638] text-white"><SelectValue placeholder={t.pick} /></SelectTrigger>
        <SelectContent className="bg-[#121721] border-[#1E2638] text-white">
          {players.map((p) => <SelectItem key={p.id} value={p.id} data-testid={`${testid}-option-${p.id}`}>{p.full_name} · {p.position} ({p.team})</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );

  const A = data?.a, B = data?.b;
  const head = (p, side) => p && (
    <div className="text-center" data-testid={`compare-head-${side}`}>
      <div className="font-display font-black text-2xl">{p.summary.full_name}</div>
      <div className="text-[10px] font-mono-tech uppercase text-slate-400">{p.summary.position} · {p.summary.alerts.length} {t.alerts}</div>
      {p.summary.alerts.length > 0 && <div className="mt-1 flex flex-wrap justify-center gap-1">{p.summary.alerts.map((al) => <span key={al.zone} className="px-1.5 py-0.5 rounded bg-[#FF2E54]/20 text-[#FF2E54] text-[10px] font-mono-tech">{zoneLabel(al.zone, lang)} ×{al.streak}</span>)}</div>}
    </div>
  );

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="club-compare-page">
      <div className="text-[10px] font-mono-tech tracking-[0.3em] text-emerald-400">{t.kicker}</div>
      <h1 className="font-display font-black uppercase text-4xl sm:text-5xl mt-2" data-testid="club-compare-title">{t.title}</h1>
      <p className="text-slate-400 mt-2">{t.subtitle}</p>

      <div className="mt-6 flex flex-wrap items-end gap-4">
        <Picker value={a} onChange={setA} testid="compare-select-a" label={t.playerA} />
        <ArrowLeftRight className="w-6 h-6 text-[#00F076] mb-2 hidden sm:block" />
        <Picker value={b} onChange={setB} testid="compare-select-b" label={t.playerB} />
      </div>

      {data && (
        <div className="mt-8 space-y-4" data-testid="compare-results">
          <div className="grid grid-cols-3 items-center">
            {head(A, "a")}<div />{head(B, "b")}
          </div>
          <Block title={t.scores} keys={["readiness", "mental", "physical", "injury_risk"]} a={A.summary.scores || {}} b={B.summary.scores || {}} t={t} testid="compare-block-scores" />
          <Block title={t.week} keys={["entries", "mood", "energy", "soreness", "stress", "sleep_hours", "pain_days_30"]} a={A.daily7} b={B.daily7} privA={A.private.daily} privB={B.private.daily} t={t} testid="compare-block-week" />
          <Block title={t.stats} keys={["top_speed_kmh", "endurance_vo2", "explosive_power", "muscle_index", "weight_kg", "height_cm"]} a={A.stats} b={B.stats} privA={A.private.stats} privB={B.private.stats} t={t} testid="compare-block-stats" />
          <Block title={t.medical} keys={["total_injuries", "active_injuries", "weaknesses"]} a={A.medical} b={B.medical} privA={A.private.medical} privB={B.private.medical} t={t} testid="compare-block-medical" />
          {(A.medical?.history?.length || B.medical?.history?.length) ? (
            <div className="grid grid-cols-2 gap-4">
              {[A, B].map((p, i) => (
                <div key={i} className="rounded-xl border border-[#1E2638] bg-[#121721] p-4">
                  <div className="text-[10px] font-mono-tech uppercase text-slate-400 mb-2">{t.history}</div>
                  {(p.medical?.history || []).map((h, j) => <div key={j} className="text-xs text-slate-300 flex justify-between gap-2 py-0.5"><span>{zoneLabel(h.zone, lang)} · {h.label}</span><span className={h.severity === 0 ? "text-[#00F076]" : "text-amber-400"}>{h.date} · {h.severity}</span></div>)}
                  {!p.medical?.history?.length && <div className="text-xs text-slate-600">{t.none}</div>}
                </div>
              ))}
            </div>
          ) : null}
          <Block title={t.video} keys={["count", "technique", "decision_making", "physical"]} a={A.video} b={B.video} privA={A.private.videos} privB={B.private.videos} t={t} testid="compare-block-video" />
        </div>
      )}
    </div>
  );
}
